import i18n from '@/i18n';
/**
 * Studio 原图上限：B2 起 null / 非正数回落到会话上限 500（禁止无限）。
 * 超额必须整批拦截并提示，禁止 files.slice(0, room) 静默截断。
 */
import { MAX_UPLOAD_FILES_PER_SESSION, resolveUploadMaxSources } from '@/utils/uploadSessionLimits';

export function resolveStudioSourceCap(maxSources) {
  return resolveUploadMaxSources(maxSources);
}

export function studioSourceRoom(maxSources, currentCount = 0) {
  const cap = resolveStudioSourceCap(maxSources);
  return Math.max(0, cap - Math.max(0, Number(currentCount) || 0));
}

export function canAddStudioSources(maxSources, currentCount = 0) {
  return studioSourceRoom(maxSources, currentCount) > 0;
}

/** @deprecated 禁止用于上传入口；请用 {@link assertStudioSourceBatch} */
export function clampStudioSources(list, maxSources) {
  const items = Array.isArray(list) ? list : [];
  const cap = resolveStudioSourceCap(maxSources);
  return items.slice(0, cap);
}

export function formatStudioSourceLimitHint(maxSources) {
  const cap = resolveStudioSourceCap(maxSources);
  return i18n.t('workbench:ipeUtilError.dragPasteMax', { cap });
}

export function formatStudioSourceCountMeta(selectedCount, totalCount, maxSources) {
  const cap = resolveStudioSourceCap(maxSources);
  return i18n.t('workbench:ipeUtilError.selectedOfCap', { selected: selectedCount, total: totalCount, cap });
}

export function studioSourceLimitToast(maxSources) {
  const cap = resolveStudioSourceCap(maxSources);
  return i18n.t('workbench:ipeUtilError.batchMaxImages', { cap, max: MAX_UPLOAD_FILES_PER_SESSION });
}

/**
 * 上传/选图超额校验：超过剩余可加数量则整批拒绝（不静默截断）。
 * @param {number} incomingCount
 * @param {number|null|undefined} maxSources
 * @param {number} [currentCount=0]
 * @returns {{ ok: true, room: number, cap: number } | { ok: false, message: string, room: number, cap: number }}
 */
export function assertStudioSourceCapacity(incomingCount, maxSources, currentCount = 0) {
  const incoming = Math.max(0, Number(incomingCount) || 0);
  const current = Math.max(0, Number(currentCount) || 0);
  const cap = resolveStudioSourceCap(maxSources);
  const room = Math.max(0, cap - current);

  if (incoming <= 0) {
    return { ok: false, message: i18n.t('workbench:ipeUtilError.selectImageFileOnly'), room, cap };
  }
  if (room <= 0) {
    return { ok: false, message: studioSourceLimitToast(maxSources), room, cap };
  }
  if (incoming > room) {
    const message = current > 0
      ? `本次选择 ${incoming} 张，当前还可添加 ${room} 张（上限 ${cap}）。请减少后重试`
      : `单次最多 ${cap} 张图片，本次选择了 ${incoming} 张。请减少后重试`;
    return { ok: false, message, room, cap };
  }
  return { ok: true, room, cap };
}

/**
 * @template T
 * @param {ArrayLike<T>|T[]} items
 * @param {number|null|undefined} maxSources
 * @param {number} [currentCount=0]
 * @returns {{ ok: true, items: T[], room: number, cap: number } | { ok: false, message: string, room: number, cap: number }}
 */
export function assertStudioSourceBatch(items, maxSources, currentCount = 0) {
  const list = Array.isArray(items) ? items : Array.from(items || []);
  const check = assertStudioSourceCapacity(list.length, maxSources, currentCount);
  if (!check.ok) {
    return { ok: false, message: check.message, room: check.room, cap: check.cap };
  }
  return { ok: true, items: list, room: check.room, cap: check.cap };
}
